<script setup lang="ts">
import type {EverContactInfo, EverDetailInfo, EverFormModel} from '@/types';

const currentStep = ref(0);
const maxStep = ref(1);
const contactInfoComponent = ref();
const eventDetailsComponent = ref();

const isSubmitting = ref(false);
const signupSuccess = ref(false);
const signupError = ref(false);

const eventDetails = ref<EverDetailInfo>({
  dates: [],
  numberGuests: 0,
  eventType: '',
});

const signupFormData = ref<EverContactInfo>({
  firstname: '',
  lastname: '',
  email: '',
  consent: true,
  phone: '',
  country: '',
  firstPartnerName: '',
  secondPartnerName: '',
});

const everFormData = ref<EverFormModel>({...eventDetails.value, ...signupFormData.value});

const {sendScreenView} = useAnalytics();

const appMounted = useState('appMounted');

if (appMounted.value) {
  const {sendScreenView} = useAnalytics();
  sendScreenView({type: 'view', target: `ever-form`}, true);
}

watch(appMounted, () => {
  const {sendScreenView} = useAnalytics();
  sendScreenView({type: 'view', target: 'ever'}, true);
});

function startForm() {
  window.scrollTo(0, 0);
  currentStep.value = 1;
  maxStep.value = 1;
}

function onStepChange(step: number) {
  if (step <= maxStep.value) {
    currentStep.value = step;
    window.scrollTo(0, 0);
  }
}

function previousStep() {
  currentStep.value--;
  window.scrollTo(0, 0);
}

function onEventDetailsUpdate(details: EverDetailInfo) {
  eventDetails.value = details;
}

function validateStep() {
  if (currentStep.value === 1) {
    if (eventDetailsComponent.value.validate()) {
      window.scrollTo(0, 0);
      currentStep.value++;
      maxStep.value = currentStep.value;
    }
  } else if (currentStep.value === 2) {
    if (contactInfoComponent.value.validate()) {
      window.scrollTo(0, 0);
      everFormData.value = {...eventDetails.value, ...signupFormData.value};
      isSubmitting.value = true;
      submitForm();
    }
  }
}

async function submitForm() {
  isSubmitting.value = true;
  const formData = buildFormData();
  sendScreenView({type: 'submit', target: 'wedding-signup'}, true);
  try {
    const config = useRuntimeConfig();
    const {submitForm} = useMailChimp();
    await submitForm(config.public.mailChimpWeddingFormId, config.public.mailChimpWeddingFormFid, formData);
  } catch {
    signupError.value = true;
  }

  signupSuccess.value = true;
  isSubmitting.value = false;
  currentStep.value = 3;
}

function buildFormData() {
  const data: {[key: string]: string | number} = {
    CONSENT: 1,
    NOGUEST: eventDetails.value.numberGuests,
    EVENTTYPE: eventDetails.value.eventType,
    FNAME: signupFormData.value.firstname,
    LNAME: signupFormData.value.lastname,
    EMAIL: signupFormData.value.email,
    PHONE: signupFormData.value.phone,
    COUNTRY: signupFormData.value.country,
    PARTNER1: signupFormData.value.firstPartnerName,
    PARTNER2: signupFormData.value.secondPartnerName,
  };

  eventDetails.value.dates.forEach((date, index) => {
    data['DATE' + (index + 1)] = new Date(date.date).toDateString();
    data['TIME' + (index + 1)] = date.time;
  });

  const formData = new FormData();
  for (const key in data) {
    formData.append(key, data[key]);
  }

  formData.append('tags', '40191911');

  return formData;
}

function resetForm() {
  currentStep.value = 0;

  eventDetails.value = {
    dates: [],
    numberGuests: 0,
    eventType: '',
  };

  signupFormData.value = {
    firstname: '',
    lastname: '',
    email: '',
    consent: true,
    phone: '',
    country: '',
    firstPartnerName: '',
    secondPartnerName: '',
  };

  everFormData.value = {...eventDetails.value, ...signupFormData.value};
}

onBeforeUnmount(() => {
  resetForm();
});
</script>

<template>
  <main>
    <div class="ever-page">
      <EverFormProgressBar :current-step="currentStep" @step-change="onStepChange" />

      <section>
        <EverBaseLayout>
          <template #column-left>
            <EverIntroductionStep v-if="currentStep === 0" @start="startForm" />

            <EverFormEventDetails v-if="currentStep === 1" ref="eventDetailsComponent" v-model="eventDetails" @update:model-value="onEventDetailsUpdate" />

            <EverFormContactInfo v-if="currentStep === 2 && !isSubmitting" ref="contactInfoComponent" v-model="signupFormData" />

            <div v-if="isSubmitting" class="form-submitting-block">
              <FormsSubmitAnimation />
            </div>

            <EverFormSummary v-if="currentStep === 3" v-model="everFormData" @unmount="resetForm" />

            <div v-if="currentStep > 0 && currentStep !== 3 && !isSubmitting" class="form-actions">
              <UiBackButton class="callout-button" @click="previousStep" />
              <TheButton v-if="currentStep == 1" tag="button" class="!bg-[var(--ever-color)] hover:!border-[var(--ever-color)] hover:!bg-white" @click="validateStep">Next</TheButton>
              <TheButton v-if="currentStep == 2" type="submit" tag="button" class="!bg-[var(--ever-color)] hover:!border-[var(--ever-color)] hover:!bg-white" @click="validateStep">Send</TheButton>
            </div>

            <div v-if="!isSubmitting" class="disclaimers">
              <p class="disclaimer">Reserving your date via this form is only an expression of interest and will sign you up for our mailing list.</p>

              <p v-if="currentStep === 2" class="disclaimer">
                Information is submitted to both OTS and to our tenant who will be operating this experience. See
                <a href="/privacy" target="_blank" class="dark-link" aria-label="Read privacy notice">OTS Privacy Notice</a>
                and the notice of the tenant experience.
              </p>

              <p v-if="currentStep === 3" class="disclaimer">
                Have questions about how to celebrate with us? Connect with our concierge at
                <a href="mailto:Ever@OneTimesSquare.com" class="dark-link">Ever@OneTimesSquare.com</a>
              </p>
            </div>
          </template>

          <template #column-right>
            <NuxtImg :src="`${useCdnUrl()}/assets/ever/ever-poster.webp`" alt="Ever feature image" />
          </template>
        </EverBaseLayout>
      </section>
    </div>
  </main>
</template>

<style lang="scss">
@use '@/assets/scss/queries';

.ever-page {
  background-color: white;
  display: flex;
  flex-direction: column;
  height: auto;
  margin-top: var(--header-desktop-collapse-height);

  .disclaimers {
    display: flex;
    flex-direction: column;
    font-style: italic;
    gap: 0.25rem;
    text-align: center;

    .disclaimer {
      font-size: 1.3rem;

      @media (max-width: 600px) {
        font-size: 2rem;
      }
    }
    .dark-link {
      color: black;
      text-decoration: underline;
    }
  }

  @include queries.for-size(ipadpro-portrait-down) {
    height: auto;
  }

  @include queries.for-size(phone-only) {
    margin-top: calc(var(--header-mobile-collapse-height) + 10px);
  }

  section {
    height: 100%;
    margin: auto;
    min-height: 100dvh;
    overflow: hidden;
    padding: 0;
    position: relative;
    width: 100%;

    .form-actions {
      display: flex;
      justify-content: space-between;
      justify-self: flex-end;
      margin-top: auto;

      text-align: center;
      width: 100%;

      @include queries.for-size(ipadpro-portrait-down) {
        margin-top: 0;
      }

      .the-button {
        background-color: var(--ever-primary-color);
        border-color: var(--ever-primary-color);
      }
    }

    .form-submitting-block {
      align-items: center;
      display: flex;
      height: 100dvh;
      justify-content: center;
      left: 0;
      position: absolute;
      top: 0;
      width: 100%;
      z-index: 100;
    }
  }
}
</style>
